"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/**
 * Tracker de page 404 côté client.
 *
 * Pourquoi côté client et pas server-side ?
 *   Next.js peut rendre `not-found.tsx` à plusieurs occasions :
 *     1. Vrai 404 — route inexistante OU `notFound()` du Page server component
 *     2. Sub-segment notFound() — un Server Component imbriqué appelle
 *        notFound() pour une donnée manquante, mais la page parente
 *        renvoie quand même 200 (le UI not-found s'affiche dans une zone)
 *     3. Streaming partiel / prerender — Next.js peut évaluer not-found
 *        en fallback sans renvoyer 404 au browser
 *
 *   Tracker server-side dans not-found.tsx déclenche aux 3 cas → pollue
 *   les stats avec des "404" sur des pages qui retournent 200 (cf bug
 *   2026-05-10 où /, /medicaments, /pharmacie-de-garde apparaissaient
 *   comme top 404).
 *
 *   Côté client, on est SÛR que le browser charge effectivement le UI 404 :
 *   le navigateur a reçu le HTML, navigue dessus, JS hydrate, useEffect
 *   fire avec le pathname réel.
 *
 * Le path tracké = `usePathname()` qui est le path effectif vu par le user.
 * Le payload utilise `keepalive: true` pour survivre à un éventuel back nav.
 */
export function NotFoundTracker() {
  const pathname = usePathname();
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    if (!pathname) return;
    sent.current = true;

    // Même chaînage referrer que <PageViewTracker /> (clé sessionStorage
    // partagée "sahha-last-path") — fix 2026-07-21, cf commentaire détaillé
    // dans PageViewTracker.tsx. Sans ça le referer stocké en DB pour un hit
    // 404 est toujours auto-référent, ce qui casse silencieusement le
    // tableau admin "Sources des 404".
    let referrer: string | null = null;
    try {
      referrer = window.sessionStorage.getItem("sahha-last-path") || document.referrer || null;
      window.sessionStorage.setItem("sahha-last-path", pathname);
    } catch {
      // ignore
    }

    try {
      fetch("/api/track/pageview", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ path: pathname, type: "NOT_FOUND", referrer }),
        keepalive: true,
        credentials: "same-origin",
      }).catch(() => {});
    } catch {
      // ignore
    }
  }, [pathname]);

  return null;
}
